import { useQuery } from "@tanstack/react-query";
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { api } from "../api/client";

export type PaletteItem = { id: string; label: string; hint?: string | null; to: string; group: string };
type SearchResponse = { q: string; groups: { group: string; hits: { id: string; label: string; hint: string | null; to: string }[] }[] };

/** Every query word must appear in the label, hint or group. */
export function matches(item: PaletteItem, query: string): boolean {
  const hay = `${item.label} ${item.hint ?? ""} ${item.group}`.toLowerCase();
  return query.toLowerCase().split(/\s+/).filter(Boolean).every((w) => hay.includes(w));
}

function useDebounced(value: string, ms: number): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Cmd/Ctrl+K: jump to a console page or find a record (customers, customer people, shipments by BOL/PRO, tickets,
 * articles, leads). Records come from /v1/console/search, which only searches what the signed-in role can read.
 * Modal combobox (WAI-ARIA 1.2): arrows move, Enter opens, Escape closes and returns focus.
 */
export function CommandPalette({ open, onClose, pages }: { open: boolean; onClose: () => void; pages: readonly PaletteItem[] }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const returnTo = useRef<Element | null>(null);
  const listId = useId();
  const term = useDebounced(query.trim(), 200);
  const remote = useQuery({
    queryKey: ["console", "search", term],
    queryFn: () => api<SearchResponse>(`/v1/console/search?q=${encodeURIComponent(term)}`),
    enabled: open && term.length >= 2,
    staleTime: 15_000,
  });

  const results = useMemo(() => {
    const local = pages.filter((p) => matches(p, query)).slice(0, query.trim() ? 6 : 50);
    const records: PaletteItem[] = term.length >= 2 && remote.data ? remote.data.groups.flatMap((g) => g.hits.map((h) => ({ ...h, id: `${g.group}:${h.id}`, group: g.group }))) : [];
    return [...records, ...local];
  }, [pages, query, term, remote.data]);

  useEffect(() => {
    if (!open) return;
    returnTo.current = document.activeElement;
    setQuery("");
    setActive(0);
    input.current?.focus();
    return () => {
      if (returnTo.current instanceof HTMLElement) returnTo.current.focus();
    };
  }, [open]);
  useEffect(() => setActive(0), [results.length, term]);

  if (!open) return null;
  const go = (item: PaletteItem | undefined) => {
    if (!item) return;
    onClose();
    navigate(item.to);
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (results.length ? (i + 1) % results.length : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "Tab") {
      e.preventDefault();
    }
  };
  const searching = term.length >= 2 && remote.isFetching;
  let lastGroup = "";
  return (
    <div className="ffa-cmdk-layer">
      <button type="button" className="ffa-cmdk-scrim" aria-label="Close search" tabIndex={-1} onClick={onClose} />
      <div className="ffa-cmdk" role="dialog" aria-modal="true" aria-label="Search the console">
        <div className="ffa-cmdk__field">
          <svg viewBox="0 0 20 20" aria-hidden="true" className="ffa-cmdk__glass">
            <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
            <path d="M13.2 13.2 17 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
          <input
            ref={input}
            className="ffa-cmdk__input"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={results[active] ? `${listId}-${active}` : undefined}
            aria-autocomplete="list"
            aria-label="Search customers, shipments, tickets, articles, leads and pages"
            placeholder="Customer, BOL/PRO, ticket #, article, lead or page…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
            maxLength={100}
          />
          <kbd>Esc</kbd>
        </div>
        <ul id={listId} role="listbox" aria-label="Results" className="ffa-cmdk__list">
          {results.map((r, i) => {
            const head = r.group !== lastGroup ? r.group : null;
            lastGroup = r.group;
            return (
              <li key={r.id} role="presentation">
                {head ? <div className="ffa-cmdk__group" aria-hidden="true">{head}</div> : null}
                <div id={`${listId}-${i}`} role="option" aria-selected={i === active} className="ffa-cmdk__item" onMouseMove={() => setActive(i)} onClick={() => go(r)}>
                  <span className="ffa-cmdk__label">{r.label}</span>
                  {r.hint ? <span className="ffa-cmdk__hint">{r.hint}</span> : null}
                </div>
              </li>
            );
          })}
        </ul>
        <p className="ffa-cmdk__status" role="status">
          {searching ? "Searching…" : remote.isError ? "Record search is unavailable right now; pages still work." : results.length === 0 ? `Nothing matches “${query}”.` : ""}
        </p>
        <div className="ffa-cmdk__foot" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> Move</span>
          <span><kbd>↵</kbd> Open</span>
          <span><kbd>Esc</kbd> Close</span>
        </div>
      </div>
    </div>
  );
}
